import { useSessionContext } from '@/contexts/session/context';
import { genId, safeJson } from '@/scripts/utils';
import { isInitial } from '@/configs/apis/user';
import { groupsApis } from '@/configs/apis/groups';
import type {
  ChatMediaInfoPartial,
  MediaCallParams,
} from '../hooks/useChatMediaCall/types';
import {
  ChatMediaType,
  type MediaCallOperateParams,
} from '../hooks/useChatMediaCall/types';
import {
  type ChatMessagesArgMessageIdParams,
  ChatMessageOwnerId,
  ChatMessagePayloadType,
} from '../hooks/useChatMessages/types';
import type { ChatContextHooksFull, ChatContextHooksProps } from '../../context';
import { eachMessagesArgs } from '../hooks/useChatMessages/utils';
import type { Chat } from '../hooks/useChats/types';

export type UseChatMediaGroupValueResult = ReturnType<typeof useChatMediaGroupValue>;

export function useChatMediaGroupValue({ hooks }: ChatContextHooksProps) {
  const {
    session: { user },
  } = useSessionContext();

  const { chats, chatMediaCall } = hooks;

  const mediaOperateOfCallGroup = useCallback(
    async ({
      chat_id,
      payload_type,
      room_id,
      type = ChatMediaType.GROUP,
      callMembers = [],
    }: MediaCallOperateParams) => {
      const { chatMediaValue } = hooks as ChatContextHooksFull;
      const chat = await chats.getChat({ chat_id });
      if (chat && user) {
        await chatMediaValue.sendMediaOperateOfCall({
          chatId: chat_id,
          groupId: chat_id,
          cliMsgId: genId(),
          opeType: payload_type,
          roomId: room_id,
          targetIds: callMembers.map((item) => item.userId),
          originUid: user.userId,
          busy: type,
        });

        chatMediaCall.setChatMediaInfo({
          [chat_id]: {
            type,
            caller: user,
            chat,
            payload_type,
            owner_id: user.userId,
            callMembers: [{ ...user }, ...callMembers],
          },
        });
      }
    },
    [chats, hooks, user, chatMediaCall]
  );

  const mediaCallGroup = useCallback(
    async (params: MediaCallParams) => {
      const { chatMediaValue } = hooks as ChatContextHooksFull;

      chatMediaCall.activeChatMedia(params.chat_id);

      await chatMediaValue.mediaCall(
        { ...params, related_gid: params.chat_id },
        {
          mediaOperateOfCall: mediaOperateOfCallGroup,
        }
      );
    },
    [chatMediaCall, hooks, mediaOperateOfCallGroup]
  );

  const mediaNoticeGroup = useCallback(
    async (params: ChatMessagesArgMessageIdParams[]) => {
      if (isInitial) {
        await eachMessagesArgs(params, async (msg) => {
          const payload = safeJson(msg.payload!, {
            room_id: '',
            busy: 0,
            group_id: '',
            sender_id: '',
          });
          const chat_id = payload.group_id;
          if (
            payload.room_id &&
            payload.busy !== ChatMediaType.SINGLE &&
            msg.owner_id === ChatMessageOwnerId.SYSTEM_PERSONAL_NOTICE
          ) {
            const { chatMediaValue, groupChatsValue } = hooks as ChatContextHooksFull;
            const callingInfo = await chatMediaValue.getMediaCallingInfoFull({
              room_id: payload.room_id,
            });

            if (callingInfo) {
              const { chat } = await groupChatsValue.loadGroupChatsByChatId({
                chat_id,
              });
              if (chat) {
                if (!chatMediaCall.chatMediaInfo) {
                  chatMediaCall.activeChatMedia(chat_id);
                }

                chatMediaCall.setChatMediaInfo({
                  [chat_id]: {
                    ...callingInfo,
                    chat: chat as any,
                    room_id: payload.room_id,
                    payload_type: msg.payload_type as ChatMessagePayloadType,
                    owner_id: payload.sender_id,
                  },
                });
              }
            }
          }
        });
      }
    },
    [chatMediaCall, hooks]
  );

  const mediaStatusUpdateGroup = useCallback(
    async (params: ChatMessagesArgMessageIdParams[]) => {
      if (isInitial) {
        await eachMessagesArgs(params, async (msg) => {
          const { chatMediaValue, chatMediaCall } = hooks as ChatContextHooksFull;
          if (
            chatMediaCall.isMediaPayloadTypeCall(msg.payload_type) ||
            chatMediaCall.isMediaPayloadTypeStatus(msg.payload_type)
          ) {
            const payload = safeJson(msg.payload!, {
              busy: undefined,
              group_id: '',
              room_id: '',
            });

            if (payload.busy && payload.busy !== ChatMediaType.SINGLE) {
              await chatMediaValue.syncMediaCallingInfo({
                chat: {
                  chat_id: payload.group_id,
                },
                room_id: payload.room_id,
              } as any);
            }
          }
        });
      }
    },
    [hooks]
  );

  const queryGroupMediaCalling = useCallback(
    async (chats: Chat[]) => {
      const { chatMediaValue } = hooks as ChatContextHooksFull;
      const res = await groupsApis.groupMediaCalling(chats.map((chat) => chat.chat_id));
      const chatMediaInfoList = chats.reduce((acc, chat) => {
        const info = res?.list.find((item) => item.gid === chat.chat_id);
        if (info) {
          acc.push({
            chat,
            room_id: info.room_id,
            payload_type:
              info.type === 0
                ? ChatMessagePayloadType.MEDIA_AUDIO_CALL
                : ChatMessagePayloadType.MEDIA_VIDEO_CALL,
          });
        }
        return acc;
      }, [] as ChatMediaInfoPartial[]);
      chatMediaInfoList.map(async (chatMediaInfo) => {
        await chatMediaValue.syncMediaCallingInfo(chatMediaInfo);
      });
    },
    [hooks]
  );

  return useMemo(
    () => ({
      mediaCallGroup,
      mediaNoticeGroup,
      mediaStatusUpdateGroup,
      queryGroupMediaCalling,
    }),
    [mediaCallGroup, mediaNoticeGroup, mediaStatusUpdateGroup, queryGroupMediaCalling]
  );
}
